import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HINT_WIDTH, hintPlacement, type HintPlacement } from './hintPlacement'
import { hintById, type Hint, type HintId } from './hints'
import { useT } from '../i18n'

/**
 * Значок «i» у подписи элемента; по клику — облако с объяснением.
 *
 * По КЛИКУ, а не по наведению: кабинетом пользуются с планшета на мостике, где
 * наведения нет вовсе, а на десктопе всплывающее по наведению облако перекрывает
 * ровно тот ползунок, к которому тянешься.
 *
 * Облако уходит порталом в body: сайдбар прокручивается по вертикали и обрезает
 * всё, что вылезает за его борт (см. hintPlacement).
 */
export function HintDot({ id, className = '' }: { id: HintId; className?: string }) {
  const t = useT()
  const hint = hintById(id)
  const btn = useRef<HTMLButtonElement>(null)
  const bubble = useRef<HTMLDivElement>(null)
  const [place, setPlace] = useState<HintPlacement | null>(null)
  const open = place != null

  const locate = (height: number) => {
    const el = btn.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    return hintPlacement(r, { width: window.innerWidth, height: window.innerHeight }, height)
  }

  // Высота облака известна только после отрисовки (текст переносится по-разному),
  // поэтому кладём по первой оценке и тут же поправляем по факту — до кадра,
  // чтобы подсказка не прыгнула на глазах.
  useLayoutEffect(() => {
    if (!place || !bubble.current) return
    const fixed = locate(bubble.current.offsetHeight)
    if (fixed && (fixed.top !== place.top || fixed.left !== place.left)) setPlace(fixed)
  }, [open, place?.top, place?.left])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPlace(null)
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (btn.current?.contains(t) || bubble.current?.contains(t)) return
      setPlace(null)
    }
    // Прокрутка/поворот сдвигают значок из-под облака — проще закрыть, чем
    // гнаться за ним. Захват (true) нужен: прокрутка сайдбара до window не всплывает.
    const onMove = () => setPlace(null)
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={t('help.aria', { title: hint.title })}
        aria-expanded={open}
        onClick={() => setPlace(open ? null : locate(0))}
        className={[
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
          open ? 'border-sonar bg-sonar/15 text-sonar' : 'border-hull text-faint hover:border-sonar hover:text-sonar',
          className,
        ].join(' ')}
      >
        {/* «i» рисуем фигурой, а не буквой: у шрифтовой «i» точка и ножка
            стоят не по оптическому центру кегля, и в кружке 16 px это видно
            (фидбек владельца 2026-08-07). Здесь центр — ровно центр. */}
        <svg viewBox="0 0 16 16" aria-hidden className="h-full w-full">
          {/* точка 3.1…5.7 и ножка 7.6…12.9 → середина фигуры ровно 8. Просвет
              между ними держим широким: в кружке 16 px узкий зазор пропадает,
              и «i» слипается в палочку. */}
          <circle cx="8" cy="4.4" r="1.3" fill="currentColor" />
          <rect x="7" y="7.6" width="2" height="5.3" rx="1" fill="currentColor" />
        </svg>
      </button>

      {open &&
        createPortal(
          <HintBubble hint={hint} left={place.left} top={place.top} innerRef={bubble} />,
          document.body,
        )}
    </>
  )
}

/**
 * Само облако — отдельно от значка и без своего состояния: так его можно
 * поставить в демо-харнесс и снять картинкой (снимок headless-браузера не умеет
 * кликать, а всё, что появляется после первого кадра, в кадр не попадает).
 */
export function HintBubble({
  hint,
  left,
  top,
  innerRef,
}: {
  hint: Hint
  left: number
  top: number
  innerRef?: React.Ref<HTMLDivElement>
}) {
  return (
    <div
      ref={innerRef}
      role="tooltip"
      style={{ left, top, width: HINT_WIDTH }}
      className="fixed z-[60] rounded-xl border border-hull bg-deep px-3.5 py-3 shadow-console"
    >
      <div className="mb-1 text-[12px] font-semibold text-foam">{hint.title}</div>
      <div className="text-[11.5px] leading-relaxed text-mist">{hint.text}</div>
    </div>
  )
}
